import { Application } from 'pixi.js'

export interface StageOptions {
  /** Width of the PixiJS canvas in CSS pixels */
  width: number
  /** Height of the PixiJS canvas in CSS pixels */
  height: number
}

const STAGE_WIDTH = 480
const STAGE_HEIGHT = 480

export class WidgetStage {
  app: Application
  private container: HTMLDivElement | null = null
  private resizeObserver: ResizeObserver | null = null

  constructor() {
    this.app = new Application()
  }

  async init(): Promise<HTMLCanvasElement> {
    await this.app.init({
      width: STAGE_WIDTH,
      height: STAGE_HEIGHT,
      backgroundAlpha: 0,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    })

    // Create the fixed-position container
    this.container = document.createElement('div')
    this.container.id = 'holostaff-widget-root'
    Object.assign(this.container.style, {
      position: 'fixed',
      bottom: '0',
      left: '50%',
      transform: 'translateX(-50%)',
      width: `${STAGE_WIDTH}px`,
      height: `${STAGE_HEIGHT}px`,
      zIndex: '2147483646',
      pointerEvents: 'none',
      overflow: 'visible',
    })

    const canvas = this.app.canvas as HTMLCanvasElement
    Object.assign(canvas.style, {
      width: '100%',
      height: '100%',
      display: 'block',
      pointerEvents: 'auto',
    })

    this.container.appendChild(canvas)
    document.body.appendChild(this.container)

    this.setupResize()

    return canvas
  }

  private setupResize(): void {
    const onResize = () => {
      // Clamp stage width to viewport on small screens
      const vw = window.innerWidth
      const width = Math.min(STAGE_WIDTH, vw)
      const scale = width / STAGE_WIDTH
      const height = STAGE_HEIGHT * scale

      if (this.container) {
        this.container.style.width = `${width}px`
        this.container.style.height = `${height}px`
      }

      this.app.renderer.resize(STAGE_WIDTH, STAGE_HEIGHT)
    }

    window.addEventListener('resize', onResize)
    onResize()
  }

  /** The root DOM container for adding sibling elements (e.g. idle video). */
  get rootElement(): HTMLDivElement | null {
    return this.container
  }

  get stageWidth(): number {
    return STAGE_WIDTH
  }

  get stageHeight(): number {
    return STAGE_HEIGHT
  }

  destroy(): void {
    window.removeEventListener('resize', this.setupResize)
    this.resizeObserver?.disconnect()
    this.container?.remove()
    this.app.destroy(true)
  }
}
